import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { RefreshCw, CalendarCheck, AlertCircle, LayoutDashboard, Radio, Truck } from "lucide-react";
import { Card, Btn } from "@/components/admin/ui";
import { API } from "@/config";

export const LiveRentals = () => {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const load = useCallback(() => axios.get(`${API}/admin/live-rentals`).then(r => setData(r.data)), []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  if (!data) return <div className="text-[#B9C8D5]">Loading…</div>;

  // Five sections in the order a driver hits them during the day:
  // overdue (yesterday's problem), due back today, delivery today,
  // currently out, upcoming (future).
  const buckets = [
    { key: "overdue", label: "Overdue", tone: "border-red-500/50", accent: "text-red-300", icon: AlertCircle, items: data.overdue, testid: "live-overdue" },
    { key: "due_today", label: "Due back today", tone: "border-amber-500/50", accent: "text-amber-300", icon: CalendarCheck, items: data.due_today, testid: "live-due-today" },
    { key: "delivery_today", label: "Delivery today", tone: "border-emerald-500/50", accent: "text-emerald-300", icon: Truck, items: data.delivery_today, testid: "live-delivery-today" },
    { key: "out_now", label: "Currently out", tone: "border-cyan-500/50", accent: "text-cyan-300", icon: Radio, items: data.out_now, testid: "live-out-now" },
    { key: "upcoming", label: "Upcoming (future)", tone: "border-sky-500/50", accent: "text-sky-300", icon: LayoutDashboard, items: data.upcoming, testid: "live-upcoming" },
  ];

  const openReservation = (id) => nav(`/admin/reservations?highlight=${id}`);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-serif text-4xl">Live Rentals</h1>
        <Btn variant="ghost" onClick={load}><RefreshCw className="w-3.5 h-3.5" /> Refresh</Btn>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        {buckets.map(b => (
          <Card key={b.key} className={`p-6 ${b.tone}`} data-testid={b.testid}>
            <div className="flex items-center gap-2 mb-4">
              <b.icon className={`w-4 h-4 ${b.accent}`} />
              <span className="eyebrow">{b.label}</span>
              <span className="text-xs text-[#B9C8D5] ml-auto">{b.items.length}</span>
            </div>
            <div className="space-y-3 max-h-[420px] overflow-auto">
              {b.items.length === 0 && <div className="text-sm text-[#B9C8D5] text-center py-6">Nothing here.</div>}
              {b.items.map(r => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => openReservation(r.id)}
                  className="w-full text-left bg-black/30 rounded-lg p-3 border border-[#FFD166]/15 hover:border-[#FFD166]/60 transition-colors"
                  data-testid={`${b.testid}-${r.id}`}
                >
                  <div className="flex justify-between items-baseline">
                    <span className="font-serif text-lg">{r.customer_name}</span>
                    <span className="text-[#FFD166]">${Number(r.total).toFixed(2)}</span>
                  </div>
                  <div className="text-xs text-[#B9C8D5]">{r.delivery_resort}</div>
                  <div className="text-xs mt-1">
                    <span className="text-[#FFD166]">Deliver:</span> {r.delivery_date}
                    {r.pickup_date && (<>· <span className="text-[#FFD166]">Return:</span> {r.pickup_date}</>)}
                  </div>
                  <div className="text-xs text-[#B9C8D5] mt-1">
                    {r.line_items?.map(li => `× ${li.quantity} ${li.name}`).join(" · ")}
                  </div>
                </button>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
